/**
 * SalesFunnelBreakdown — Aggregate company-wide sales funnel.
 *
 * MOVED FROM: Intelligence Dashboard "Sales Performance" tab.
 * NEW HOME:   Command Dashboard (which owns aggregate sales metrics).
 *
 * Funnel stages (preserved from prior surface):
 *   leads → contacted → quoted → won → revenue
 *
 * CANONICAL REP RESOLUTION (read-side only):
 *   Stored fields (`opportunity.assignedRep`, `quote.createdBy`,
 *   `lead.assignedRep`) are read as-is. At display/aggregation
 *   time we bucket any non-canonical creator name as "Unassigned".
 *   We do NOT mutate stored data, seed data, or write paths.
 *   We do NOT use useCurrentRep() for attribution.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Target, Users, BarChart3, TrendingUp, ArrowRight } from "lucide-react";
import { useAppData } from "@/data/AppDataContext";
import { isWonStatus } from "@/data/quoteTypes";
import { supabase } from "@/lib/supabase";
import { isUuidLike, resolveProfileDisplayName } from "@/lib/profileDisplayName";

function resolveRepDisplayName(
  value: string | undefined | null,
  repNameById: Map<string, string>,
): string {
  const resolved = resolveProfileDisplayName(value, repNameById, "Unassigned");
  return String(resolved ?? "").trim() || "Unassigned";
}

function isMissingRep(value: string | undefined | null): boolean {
  const normalized = String(value ?? "").trim().toLowerCase();
  return (
    normalized.length === 0 ||
    normalized === "unassigned" ||
    normalized === "unknown" ||
    normalized === "n/a" ||
    normalized === "na" ||
    normalized === "null" ||
    normalized === "undefined" ||
    normalized === "—" ||
    normalized === "-"
  );
}

interface RepRow {
  rep: string;
  leads: number;
  contacted: number;
  quoted: number;
  won: number;
  revenue: number;
  closeRate: number;
  avgDeal: number;
}

export function SalesFunnelBreakdown() {
  const { quotes, leads, appointmentResults, salesOpportunities } = useAppData();
  const [repNameById, setRepNameById] = useState<Map<string, string>>(new Map());

  useEffect(() => {
    let cancelled = false;
    const loadRepNames = async () => {
      const repIds = Array.from(
        new Set(
          [
            ...leads.map((l) => String((l as { assignedRep?: string }).assignedRep ?? "").trim()),
            ...quotes.map((q) => String((q as { createdBy?: string }).createdBy ?? "").trim()),
            ...salesOpportunities.map((o) => String(o.assignedRep ?? "").trim()),
            ...appointmentResults.map((r) => String(r.assignedRep ?? "").trim()),
          ].filter((v) => isUuidLike(v)),
        ),
      );

      if (repIds.length === 0) {
        setRepNameById(new Map());
        return;
      }

      const [byIdRes, byAuthIdRes] = await Promise.all([
        supabase
          .from("profiles")
          .select("id, auth_user_id, full_name, first_name, last_name")
          .in("id", repIds),
        supabase
          .from("profiles")
          .select("id, auth_user_id, full_name, first_name, last_name")
          .in("auth_user_id", repIds),
      ]);

      if (cancelled) return;

      const map = new Map<string, string>();
      const rows = [...(byIdRes.data ?? []), ...(byAuthIdRes.data ?? [])];
      for (const row of rows) {
        const first = String(row.first_name ?? "").trim();
        const last = String(row.last_name ?? "").trim();
        const fullName = String(row.full_name ?? "").trim() || [first, last].filter(Boolean).join(" ").trim();
        const id = String(row.id ?? "").trim();
        const authUserId = String(row.auth_user_id ?? "").trim();
        if (!fullName) continue;
        if (id) map.set(id, fullName);
        if (authUserId) map.set(authUserId, fullName);
      }
      setRepNameById(map);
    };

    void loadRepNames();
    return () => {
      cancelled = true;
    };
  }, [leads, quotes, salesOpportunities, appointmentResults]);

  const { reps, totals } = useMemo(() => {
    const map = new Map<string, RepRow>();
    const ensure = (name: string): RepRow => {
      const existing = map.get(name);
      if (existing) return existing;
      const fresh: RepRow = {
        rep: name, leads: 0, contacted: 0, quoted: 0, won: 0,
        revenue: 0, closeRate: 0, avgDeal: 0,
      };
      map.set(name, fresh);
      return fresh;
    };

    const isContactedStage = (stageRaw: string | null | undefined): boolean => {
      const stage = String(stageRaw ?? "").trim().toLowerCase();
      return stage.length > 0 && stage !== "lead";
    };

    const isQuotedStage = (stageRaw: string | null | undefined): boolean => {
      const stage = String(stageRaw ?? "").trim().toLowerCase();
      return stage !== "" && stage !== "lead";
    };

    const isWonLeadStage = (stageRaw: string | null | undefined): boolean =>
      String(stageRaw ?? "").trim().toLowerCase() === "closed won";

    const parseTs = (value: string | undefined | null): number => Date.parse(String(value ?? "")) || 0;
    const leadKeys = (lead: { id?: string; leadId?: string }) =>
      Array.from(
        new Set(
          [String(lead.leadId ?? "").trim(), String(lead.id ?? "").trim()].filter(Boolean),
        ),
      );

    // Revenue should come from won quotes tied to the same lead.
    const wonQuoteRevenueByLead = new Map<string, number>();
    const wonQuoteTimestampByLead = new Map<string, number>();
    const quoteTimestamp = (q: (typeof quotes)[number]): number =>
      parseTs(
        String(
          (q as { updatedAt?: string; updated_at?: string; createdAt?: string; created_at?: string }).updatedAt ??
            (q as { updated_at?: string }).updated_at ??
            (q as { createdAt?: string }).createdAt ??
            (q as { created_at?: string }).created_at ??
            "",
        ),
      );

    const latestQuoteRepByLead = new Map<string, { rep: string; ts: number }>();
    const hasQuotedByLead = new Map<string, boolean>();
    const hasWonByLead = new Map<string, boolean>();

    for (const q of quotes) {
      const leadId = String(
        (q as { leadId?: string; lead_id?: string }).leadId ??
          (q as { lead_id?: string }).lead_id ??
          "",
      ).trim();
      if (!leadId) continue;

      const ts = quoteTimestamp(q);
      const rep = resolveRepDisplayName((q as { createdBy?: string }).createdBy, repNameById);
      const prev = latestQuoteRepByLead.get(leadId);
      if (!prev || ts >= prev.ts) latestQuoteRepByLead.set(leadId, { rep, ts });

      if (isQuotedStage(q.status)) hasQuotedByLead.set(leadId, true);
      if (isWonStatus(q.status)) hasWonByLead.set(leadId, true);
    }

    const latestApptRepByLead = new Map<string, { rep: string; ts: number }>();
    for (const r of appointmentResults) {
      const leadId = String(r.leadId ?? "").trim();
      if (!leadId) continue;
      const ts = parseTs(r.submittedAt || r.appointmentDatetime || "");
      const rep = resolveRepDisplayName(r.assignedRep, repNameById);
      const prev = latestApptRepByLead.get(leadId);
      if (!prev || ts >= prev.ts) latestApptRepByLead.set(leadId, { rep, ts });
    }

    const latestOppRepByLead = new Map<string, { rep: string; ts: number }>();
    for (const o of salesOpportunities) {
      const ts = parseTs(o.updatedAt || o.createdAt || "");
      const rep = resolveRepDisplayName(o.assignedRep, repNameById);
      const keys = Array.from(
        new Set(
          [String(o.leadId ?? "").trim(), String(o.id ?? "").trim()].filter(Boolean),
        ),
      );
      if (!keys.length) continue;
      for (const key of keys) {
        const prev = latestOppRepByLead.get(key);
        if (!prev || ts >= prev.ts) latestOppRepByLead.set(key, { rep, ts });
        if (isQuotedStage(o.stage)) hasQuotedByLead.set(key, true);
        if (isWonLeadStage(o.stage)) hasWonByLead.set(key, true);
      }
    }

    for (const q of quotes) {
      if (!isWonStatus(q.status)) continue;
      const leadId = String(
        (q as { leadId?: string; lead_id?: string }).leadId ??
          (q as { lead_id?: string }).lead_id ??
          "",
      ).trim();
      if (!leadId) continue;
      const ts = quoteTimestamp(q);
      const prevTs = wonQuoteTimestampByLead.get(leadId) ?? -1;
      if (ts >= prevTs) {
        wonQuoteTimestampByLead.set(leadId, ts);
        wonQuoteRevenueByLead.set(leadId, Number(q.totalEstimate ?? 0));
      }
    }

    leads.forEach((l) => {
      const keys = leadKeys({ id: String(l.id ?? "").trim(), leadId: String((l as { leadId?: string }).leadId ?? "").trim() });
      const leadId = keys[0] ?? "";
      const leadRep = resolveRepDisplayName((l as { assignedRep?: string }).assignedRep, repNameById);
      const quoteRep = keys.map((k) => latestQuoteRepByLead.get(k)?.rep).find((v) => !isMissingRep(v));
      const oppRep = keys.map((k) => latestOppRepByLead.get(k)?.rep).find((v) => !isMissingRep(v));
      const apptRep = keys.map((k) => latestApptRepByLead.get(k)?.rep).find((v) => !isMissingRep(v));
      const rep = !isMissingRep(leadRep)
        ? leadRep
        : !isMissingRep(quoteRep)
          ? quoteRep!
          : !isMissingRep(oppRep)
            ? oppRep!
            : !isMissingRep(apptRep)
              ? apptRep!
              : "Unassigned";
      const row = ensure(rep);
      row.leads += 1;
      if (isContactedStage(l.stage)) row.contacted += 1;
      if (keys.some((k) => hasQuotedByLead.get(k)) || isQuotedStage(l.stage)) row.quoted += 1;
      if (keys.some((k) => hasWonByLead.get(k)) || isWonLeadStage(l.stage)) {
        row.won += 1;
        const revenue = keys.map((k) => wonQuoteRevenueByLead.get(k) ?? 0).find((v) => v > 0) ?? 0;
        row.revenue += revenue;
      }
    });

    const reps = [...map.values()]
      .map((r) => ({
        ...r,
        closeRate: r.quoted > 0 ? Math.round((r.won / r.quoted) * 100) : 0,
        avgDeal: r.won > 0 ? r.revenue / r.won : 0,
      }))
      .sort((a, b) => b.revenue - a.revenue);

    const totals = {
      leads: reps.reduce((s, r) => s + r.leads, 0),
      contacted: reps.reduce((s, r) => s + r.contacted, 0),
      quoted: reps.reduce((s, r) => s + r.quoted, 0),
      won: reps.reduce((s, r) => s + r.won, 0),
      revenue: reps.reduce((s, r) => s + r.revenue, 0),
    };
    return { reps, totals };
  }, [quotes, leads, appointmentResults, salesOpportunities, repNameById]);

  const closeRate = totals.quoted > 0 ? Math.round((totals.won / totals.quoted) * 100) : 0;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-semibold flex items-center gap-2">
          <Target className="h-4 w-4 text-primary" />
          Sales Funnel Breakdown
          <Badge variant="outline" className="text-[9px] font-normal ml-1">Company-wide</Badge>
          <Link
            to="/rep-scorecards"
            className="ml-auto text-[11px] text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
          >
            Per-rep scorecards <ArrowRight className="h-3 w-3" />
          </Link>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Funnel strip */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
          <FunnelTile icon={Users} label="Leads" value={totals.leads} />
          <FunnelTile icon={Users} label="Contacted" value={totals.contacted} />
          <FunnelTile icon={BarChart3} label="Quoted" value={totals.quoted} />
          <FunnelTile icon={Target} label="Won" value={totals.won} tone={closeRate > 30 ? "good" : "warn"} />
          <FunnelTile
            icon={TrendingUp}
            label="Revenue"
            value={`$${(totals.revenue / 1000).toFixed(0)}K`}
            tone="good"
          />
        </div>

        {/* Rep contribution table */}
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-xs">Rep</TableHead>
                <TableHead className="text-xs text-right">Leads</TableHead>
                <TableHead className="text-xs text-right">Quoted</TableHead>
                <TableHead className="text-xs text-right">Won</TableHead>
                <TableHead className="text-xs text-right">Close %</TableHead>
                <TableHead className="text-xs text-right">Avg Deal</TableHead>
                <TableHead className="text-xs text-right">Revenue</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {reps.map((r) => (
                <TableRow key={r.rep}>
                  <TableCell className="text-xs font-medium">
                    {r.rep === "Unassigned" ? (
                      <span className="text-muted-foreground italic">Unassigned</span>
                    ) : (
                      r.rep
                    )}
                  </TableCell>
                  <TableCell className="text-xs text-right">{r.leads}</TableCell>
                  <TableCell className="text-xs text-right">{r.quoted}</TableCell>
                  <TableCell className="text-xs text-right">{r.won}</TableCell>
                  <TableCell
                    className={`text-xs text-right ${
                      r.closeRate > 40 ? "text-success font-medium" : r.closeRate < 20 ? "text-destructive" : ""
                    }`}
                  >
                    {r.closeRate}%
                  </TableCell>
                  <TableCell className="text-xs text-right">
                    ${r.avgDeal.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                  </TableCell>
                  <TableCell className="text-xs text-right font-medium">
                    ${r.revenue.toLocaleString()}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}

function FunnelTile({
  icon: Icon, label, value, tone = "default",
}: {
  icon: React.ElementType;
  label: string;
  value: string | number;
  tone?: "default" | "good" | "warn";
}) {
  const toneClass =
    tone === "good" ? "text-success" : tone === "warn" ? "text-warning" : "text-foreground";
  return (
    <div className="rounded-lg border bg-muted/20 p-3">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
        <Icon className="h-3 w-3" /> {label}
      </div>
      <p className={`text-lg font-bold mt-1 ${toneClass}`}>{value}</p>
    </div>
  );
}
